/**
 * useVisualViewport — Run 2 (mobile-remediation) shared primitive.
 *
 * Tracks `window.visualViewport` so overlay shells (Modal/Drawer) and sticky
 * action footers can react to the on-screen keyboard opening/closing on
 * iOS/Android. Resolves audit finding F-08: the legacy `useViewportHeight`
 * hook (`src/hooks/useViewportHeight.ts`) only listens for `resize` /
 * `orientationchange` on `window`, which do NOT fire when the keyboard opens
 * — the layout viewport is unaffected; only the *visual* viewport shrinks.
 * That hook stays in place unchanged (it drives the legacy `--vh` var some
 * older call sites rely on); this hook is the keyboard-aware upgrade new
 * overlay/footer code should use.
 *
 * Falls back to `window.innerHeight` with `keyboardInset` pinned at 0 on
 * browsers without `visualViewport` (desktop Firefox < 91, old WebKit) —
 * a no-op, not a regression.
 *
 * rAF-throttled per docs/audits/MOBILE_AUDIT.md §3 (High-Risk Areas #5): an
 * unthrottled VV listener risks layout thrash on every keyboard-drag frame.
 */
import { useEffect, useRef, useState } from 'react';

export interface VisualViewportState {
  /** Visual-viewport height in CSS px. `null` only when `window` is unavailable (SSR). */
  height: number | null;
  /**
   * Height covered by the on-screen keyboard (or other viewport-shrinking
   * chrome), in px. `0` when no `visualViewport` support or nothing is
   * currently shrinking the viewport.
   */
  keyboardInset: number;
  /**
   * Heuristic: `true` once `keyboardInset` exceeds a noise threshold. Guards
   * against false positives from URL-bar show/hide (a few px) or minor
   * rounding — a real software keyboard is reliably taller than this.
   */
  isKeyboardOpen: boolean;
}

const KEYBOARD_THRESHOLD_PX = 60;

function measure(): VisualViewportState {
  if (typeof window === 'undefined') {
    return { height: null, keyboardInset: 0, isKeyboardOpen: false };
  }
  const vv = window.visualViewport;
  const vvHeight = vv?.height ?? window.innerHeight;
  const vvOffsetTop = vv?.offsetTop ?? 0;
  const inset = Math.max(0, window.innerHeight - vvHeight - vvOffsetTop);
  return {
    height: vvHeight,
    keyboardInset: inset,
    isKeyboardOpen: inset > KEYBOARD_THRESHOLD_PX,
  };
}

const IDLE_STATE: VisualViewportState = { height: null, keyboardInset: 0, isKeyboardOpen: false };

/**
 * @param enabled Pass `false` to skip subscribing entirely (returns a static
 *   idle state instead). Overlay shells that stay mounted while closed
 *   (e.g. `<Modal open={false}>`) should pass their `open` flag here so
 *   dozens of unused instances across the app don't each hold a live
 *   `visualViewport` listener.
 */
export function useVisualViewport(enabled = true): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(() => (enabled ? measure() : IDLE_STATE));
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) {
      setState(IDLE_STATE);
      return;
    }
    const vv = window.visualViewport;

    const recompute = () => {
      rafRef.current = null;
      setState(measure());
    };

    const schedule = () => {
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(recompute);
    };

    schedule();

    if (vv) {
      vv.addEventListener('resize', schedule);
      vv.addEventListener('scroll', schedule);
    } else {
      window.addEventListener('resize', schedule);
    }
    window.addEventListener('orientationchange', schedule);

    return () => {
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      if (vv) {
        vv.removeEventListener('resize', schedule);
        vv.removeEventListener('scroll', schedule);
      } else {
        window.removeEventListener('resize', schedule);
      }
      window.removeEventListener('orientationchange', schedule);
    };
  }, [enabled]);

  return state;
}

export default useVisualViewport;
